import apiClient from "./client";
import { store } from "store";
import type {
  LoginCredentials, LoginResponse,
  PartnerEntity, ContactUser, ContactTag, Schedule, AvailabilitySchedule, AdHocAvailabilitySchedule,
  ScheduleItem, FoodLog, Upload, Note, EntityAddress, BlackoutDay, DaysOff, DaysOffStatus,
  ScheduleItemChangeRequest, ChangeRequestDetails,
  CronJobStatus, EmailLogsResponse,
} from "types";

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const { data } = await apiClient.post("/sessions", credentials);
  return data;
};

export const logout = async (): Promise<void> => {
  await apiClient.delete("/sessions");
};

export const requestPasswordReset = async (email: string): Promise<void> => {
  await apiClient.post("/password_resets", { email });
};

export const resetPassword = async (token: string, password: string, passwordConfirmation: string): Promise<void> => {
  await apiClient.patch(`/password_resets/${token}`, {
    password,
    password_confirmation: passwordConfirmation,
  });
};

export const verifyAccount = async (token: string): Promise<{ reset_token: string }> => {
  const { data } = await apiClient.post(`/verifications/${token}`, {});
  return data;
};

// ─── Partner Entities ─────────────────────────────────────────────────────────

export const fetchPartners = async (): Promise<PartnerEntity[]> => {
  const { data } = await apiClient.get("/partner_entities");
  return normalizeList<PartnerEntity>(data);
};

export const fetchPartner = async (id: number): Promise<PartnerEntity> => {
  const { data } = await apiClient.get(`/partner_entities/${id}`);
  return normalizeOne<PartnerEntity>(data);
};

export const createPartner = async (params: Partial<PartnerEntity>): Promise<PartnerEntity> => {
  const { data } = await apiClient.post("/partner_entities", { partner_entity: params });
  return normalizeOne<PartnerEntity>(data);
};

export const updatePartner = async (id: number, params: Partial<PartnerEntity>): Promise<PartnerEntity> => {
  const { data } = await apiClient.patch(`/partner_entities/${id}`, { partner_entity: params });
  return normalizeOne<PartnerEntity>(data);
};

export const deletePartner = async (id: number): Promise<void> => {
  await apiClient.delete(`/partner_entities/${id}`);
};

// ─── Entity Addresses ─────────────────────────────────────────────────────────

export const fetchAddresses = async (params?: Record<string, string>): Promise<EntityAddress[]> => {
  const { data } = await apiClient.get("/entity_addresses", { params });
  return normalizeList<EntityAddress>(data);
};

export const createAddress = async (params: Partial<EntityAddress>): Promise<EntityAddress> => {
  const { data } = await apiClient.post("/entity_addresses", { entity_address: params });
  return normalizeOne<EntityAddress>(data);
};

export const updateAddress = async (id: number, params: Partial<EntityAddress>): Promise<EntityAddress> => {
  const { data } = await apiClient.patch(`/entity_addresses/${id}`, { entity_address: params });
  return normalizeOne<EntityAddress>(data);
};

// ─── Contact Users ────────────────────────────────────────────────────────────

export const fetchUsers = async (params?: { partner_entity_id?: number | string; role?: string }): Promise<ContactUser[]> => {
  const { data } = await apiClient.get("/contact_users", { params });
  return normalizeList<ContactUser>(data);
};

export const fetchUser = async (id: number): Promise<ContactUser> => {
  const { data } = await apiClient.get(`/contact_users/${id}`);
  return normalizeOne<ContactUser>(data);
};

export const createUser = async (params: Partial<ContactUser>): Promise<ContactUser> => {
  const { data } = await apiClient.post("/contact_users", { contact_user: params });
  return normalizeOne<ContactUser>(data);
};

export const updateUser = async (id: number, params: Partial<ContactUser>): Promise<ContactUser> => {
  const { data } = await apiClient.patch(`/contact_users/${id}`, { contact_user: params });
  return normalizeOne<ContactUser>(data);
};

export const deleteUser = async (id: number): Promise<void> => {
  await apiClient.delete(`/contact_users/${id}`);
};

// ─── Contact Tags (admin only) ────────────────────────────────────────────────

export const createContactTag = async (params: {
  contact_user_id:   number;
  partner_entity_id: number;
  tag:               string;
}): Promise<ContactTag> => {
  const { data } = await apiClient.post("/contact_tags", { contact_tag: params });
  return normalizeOne<ContactTag>(data);
};

export const updateContactTag = async (id: number, tag: string): Promise<ContactTag> => {
  const { data } = await apiClient.patch(`/contact_tags/${id}`, { contact_tag: { tag } });
  return normalizeOne<ContactTag>(data);
};

export const deleteContactTag = async (id: number): Promise<void> => {
  await apiClient.delete(`/contact_tags/${id}`);
};

// ─── Auth Users ───────────────────────────────────────────────────────────────

export interface AuthUserSummary {
  id:              number;
  email:           string;
  role:            string;
  active:          boolean;
  contact_user_id: number;
  name:            string | null;
}

export const fetchAuthUsers = async (): Promise<AuthUserSummary[]> => {
  const { data } = await apiClient.get("/auth_users");
  return data.auth_users;
};

export const impersonateUser = async (authUserId: number): Promise<import("types").LoginResponse> => {
  const { data } = await apiClient.post("/tools/impersonate", { auth_user_id: authUserId });
  return data;
};

export const createAuthUser = async (
  contactUserId: number,
  email: string,
  role: string
): Promise<void> => {
  await apiClient.post("/auth_users", {
    auth_user: { contact_user_id: contactUserId, email, role },
  });
};

export const resendVerification = async (contactUserId: number): Promise<void> => {
  await apiClient.post(`/auth_users/${contactUserId}/resend_verification`);
};

export const updateAuthUser = async (
  contactUserId: number,
  role: string
): Promise<void> => {
  // The auth_users controller uses the auth_user ID, not contact_user ID.
  // We pass the contact_user_id and let the backend find the right auth_user.
  await apiClient.patch(`/auth_users/${contactUserId}`, {
    auth_user: { role },
  });
};

// ─── Schedule Items ───────────────────────────────────────────────────────────

export const fetchScheduleItems = async (params?: Record<string, string>): Promise<ScheduleItem[]> => {
  // The server computes "current week" boundaries in whatever timezone we tell it —
  // it defaults to UTC otherwise, which disagrees with the client near week
  // boundaries (e.g. Sunday evening Pacific is already Monday UTC). The client is
  // the source of truth for what "now" means to the person using it, so we always
  // send our local IANA zone name and let the server anchor to that.
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: response } = await apiClient.get("/schedule_items", { params: { timezone, ...params } });
  return parseScheduleItemsWithIncludes(response);
};

/**
 * Fetch all schedule items that have change requests.
 * Pass `changeRequestStatus` to filter to items with requests of a specific status
 * (e.g. "requested" for open-only). Omit to return items with any change request.
 */
export const fetchScheduleItemsWithChangeRequests = async (
  changeRequestStatus?: string,
): Promise<ScheduleItem[]> => {
  const params: Record<string, string> = {
    with_change_requests:   "true",
    include_change_requests: "true",
  };
  if (changeRequestStatus) params.change_request_status = changeRequestStatus;
  return fetchScheduleItems(params);
};

export const fetchScheduleItem = async (id: number): Promise<ScheduleItem> => {
  const { data: response } = await apiClient.get(`/schedule_items/${id}`);
  const item = normalizeOne<ScheduleItem>(response);

  const included: Array<{ id: string; type: string; attributes: Record<string, unknown> }> =
    response.included ?? [];
  const rels = response.data.relationships ?? {};

  const extractIncluded = <T>(type: string, ids: string[]): Array<T & { id: number }> =>
    ids
      .map((rid) => included.find((r) => r.type === type && r.id === rid))
      .filter((r): r is NonNullable<typeof r> => r != null)
      .map((r) => ({ ...(r.attributes as unknown as T), id: Number(r.id) }));

  item.food_logs = extractIncluded<FoodLog>(
    "food_log",
    ((rels.food_logs?.data ?? []) as Array<{ id: string }>).map((r) => r.id)
  );

  item.deliver_tos = extractIncluded<PartnerEntity>(
    "partner_entity",
    ((rels.deliver_tos?.data ?? []) as Array<{ id: string }>).map((r) => r.id)
  );

  item.received_froms = extractIncluded<PartnerEntity>(
    "partner_entity",
    ((rels.received_froms?.data ?? []) as Array<{ id: string }>).map((r) => r.id)
  );

  return item;
};

export const createScheduleItem = async (params: Partial<ScheduleItem>): Promise<ScheduleItem> => {
  const { data } = await apiClient.post("/schedule_items", { schedule_item: params });
  return normalizeOne<ScheduleItem>(data);
};

export const bulkCreateScheduleItems = async (
  items: Array<{ partner_entity_id: number; scheduled_at: string; status: string }>
): Promise<ScheduleItem[]> => {
  const { data } = await apiClient.post("/schedule_items/bulk_create", { schedule_items: items });
  return normalizeList<ScheduleItem>(data);
};

export const assignCourier = async (id: number, courierId: number): Promise<ScheduleItem> => {
  const { data } = await apiClient.patch(`/schedule_items/${id}/assign_courier`, { courier_id: courierId });
  return normalizeOne<ScheduleItem>(data);
};

export const cancelScheduleItem = async (id: number): Promise<ScheduleItem> => {
  const { data } = await apiClient.patch(`/schedule_items/${id}/cancel`);
  return normalizeOne<ScheduleItem>(data);
};

export const updateScheduleItem = async (id: number, params: Partial<ScheduleItem>): Promise<ScheduleItem> => {
  const { data } = await apiClient.patch(`/schedule_items/${id}`, { schedule_item: params });
  return normalizeOne<ScheduleItem>(data);
};

export const deleteScheduleItem = async (id: number): Promise<void> => {
  await apiClient.delete(`/schedule_items/${id}`);
};

// Shared parser for schedule item responses that include contact_user + change_requests
function parseScheduleItemsWithIncludes(response: {
  data: Array<{
    id: string;
    attributes: Omit<ScheduleItem, "id" | "contact_user" | "change_requests" | "deliver_tos" | "received_froms">;
    relationships?: {
      contact_user?:    { data?: { id: string } | null };
      change_requests?: { data?: Array<{ id: string }> };
      deliver_tos?:     { data?: Array<{ id: string }> };
      received_froms?:  { data?: Array<{ id: string }> };
    };
  }>;
  included?: Array<{ id: string; type: string; attributes: Record<string, unknown> }>;
}): ScheduleItem[] {
  const included = response.included ?? [];

  const contactUserMap: Record<string, ContactUser & { id: number }> = {};
  included
    .filter((r) => r.type === "contact_user")
    .forEach((r) => {
      contactUserMap[r.id] = { ...(r.attributes as unknown as ContactUser), id: Number(r.id) };
    });

  const partnerEntityMap: Record<string, PartnerEntity & { id: number }> = {};
  included
    .filter((r) => r.type === "partner_entity")
    .forEach((r) => {
      partnerEntityMap[r.id] = { ...(r.attributes as unknown as PartnerEntity), id: Number(r.id) };
    });

  const changeRequestMap: Record<string, ScheduleItemChangeRequest & { id: number }> = {};
  included
    .filter((r) => r.type === "schedule_item_change_request")
    .forEach((r) => {
      changeRequestMap[r.id] = {
        ...(r.attributes as unknown as ScheduleItemChangeRequest),
        id: Number(r.id),
      };
    });

  const extractPartnerEntities = (ids: string[]): Array<PartnerEntity & { id: number }> =>
    ids.map((rid) => partnerEntityMap[rid]).filter((r): r is PartnerEntity & { id: number } => r != null);

  return response.data.map((item) => {
    const base: ScheduleItem = { ...item.attributes, id: Number(item.id) };

    const contactUserId = item.relationships?.contact_user?.data?.id;
    if (contactUserId && contactUserMap[contactUserId]) {
      base.contact_user = contactUserMap[contactUserId];
    }

    const crIds = (item.relationships?.change_requests?.data ?? []).map((r) => r.id);
    base.change_requests = crIds
      .map((rid) => changeRequestMap[rid])
      .filter((r): r is ScheduleItemChangeRequest & { id: number } => r != null);

    base.deliver_tos    = extractPartnerEntities(
      ((item.relationships?.deliver_tos?.data   ?? []) as Array<{ id: string }>).map((r) => r.id)
    );
    base.received_froms = extractPartnerEntities(
      ((item.relationships?.received_froms?.data ?? []) as Array<{ id: string }>).map((r) => r.id)
    );

    return base;
  });
}

// Fetches items from start-of-today onwards (partner home page)
export const fetchUpcomingPickups = async (): Promise<ScheduleItem[]> => {
  const { data: response } = await apiClient.get("/schedule_items", {
    params: { from_today: "true", include_change_requests: "true" },
  });
  return parseScheduleItemsWithIncludes(response);
};

// Fetches ALL items for the partner (no time filter — for the "view all" page)
export const fetchAllPartnerPickups = async (): Promise<ScheduleItem[]> => {
  const { data: response } = await apiClient.get("/schedule_items", {
    params: { include_change_requests: "true" },
  });
  return parseScheduleItemsWithIncludes(response);
};

// ─── Change Requests ──────────────────────────────────────────────────────────

export const updateChangeRequest = async (
  scheduleItemId:  number,
  changeRequestId: number,
  status: "approved" | "denied"
): Promise<ScheduleItemChangeRequest> => {
  const { data } = await apiClient.patch(
    `/schedule_items/${scheduleItemId}/change_requests/${changeRequestId}`,
    { change_request: { status } }
  );
  return normalizeOne<ScheduleItemChangeRequest>(data);
};

export const createChangeRequest = async (
  scheduleItemId: number,
  params: {
    change_type: "time_change" | "cancellation";
    details?:    ChangeRequestDetails;
  }
): Promise<ScheduleItemChangeRequest> => {
  const { data } = await apiClient.post(
    `/schedule_items/${scheduleItemId}/change_requests`,
    { change_request: params }
  );
  return normalizeOne<ScheduleItemChangeRequest>(data);
};

// ─── Food Logs ────────────────────────────────────────────────────────────────

export const fetchFoodLogs = async (params?: Record<string, string>): Promise<FoodLog[]> => {
  const { data } = await apiClient.get("/food_logs", { params });
  return normalizeList<FoodLog>(data);
};

export const fetchFoodLog = async (id: number): Promise<FoodLog> => {
  const { data: response } = await apiClient.get(`/food_logs/${id}`);
  const log = normalizeOne<FoodLog>(response);

  const included: Array<{ id: string; type: string; attributes: Record<string, unknown> }> =
    response.included ?? [];
  const uploadIds = (
    (response.data.relationships?.uploads?.data ?? []) as Array<{ id: string }>
  ).map((r) => r.id);

  log.uploads = uploadIds
    .map((rid) => included.find((r) => r.type === "upload" && r.id === rid))
    .filter((r): r is NonNullable<typeof r> => r != null)
    .map((r) => ({ ...(r.attributes as Omit<Upload, "id">), id: Number(r.id) }));

  return log;
};

export const createFoodLog = async (params: Partial<FoodLog>): Promise<FoodLog> => {
  const { data } = await apiClient.post("/food_logs", { food_log: params });
  return normalizeOne<FoodLog>(data);
};

export const updateFoodLog = async (id: number, params: Partial<FoodLog>): Promise<FoodLog> => {
  const { data } = await apiClient.patch(`/food_logs/${id}`, { food_log: params });
  return normalizeOne<FoodLog>(data);
};

// ─── Contact User Profile Photo ───────────────────────────────────────────────

export const uploadProfilePhoto = async (contactUserId: number, file: File): Promise<ContactUser> => {
  const body = new FormData();
  body.append("file", file);
  const { data } = await apiClient.put(`/contact_users/${contactUserId}/profile_photo`, body);
  return normalizeOne<ContactUser>(data);
};

export const removeProfilePhoto = async (contactUserId: number): Promise<ContactUser> => {
  const { data } = await apiClient.delete(`/contact_users/${contactUserId}/profile_photo`);
  return normalizeOne<ContactUser>(data);
};

// ─── Uploads ──────────────────────────────────────────────────────────────────

export const createUpload = async (
  uploadableType: string,
  uploadableId:   number,
  file:           File
): Promise<Upload> => {
  const body = new FormData();
  body.append("file",            file);
  body.append("uploadable_type", uploadableType);
  body.append("uploadable_id",   String(uploadableId));
  const { data } = await apiClient.post("/uploads", body);
  return data;
};

export const deleteUpload = async (id: number): Promise<void> => {
  await apiClient.delete(`/uploads/${id}`);
};

// ─── Schedules ────────────────────────────────────────────────────────────────

export const fetchSchedules = async (params?: Record<string, string>): Promise<Schedule[]> => {
  const { data } = await apiClient.get("/schedules", { params });
  return normalizeList<Schedule>(data);
};

export const createSchedule = async (params: {
  partner_entity_id: number;
  entity_address_id: number;
  schedule: Record<string, Array<{ start: string; end: string }>>;
}): Promise<Schedule> => {
  const { data } = await apiClient.post("/schedules", { schedule: params });
  return normalizeOne<Schedule>(data);
};

export const updateSchedule = async (id: number, params: {
  partner_entity_id?: number;
  entity_address_id?: number;
  schedule: Record<string, Array<{ start: string; end: string }>>;
}): Promise<Schedule> => {
  const { data } = await apiClient.patch(`/schedules/${id}`, { schedule: params });
  return normalizeOne<Schedule>(data);
};

export const deleteSchedule = async (id: number): Promise<void> => {
  await apiClient.delete(`/schedules/${id}`);
};

// ─── Notes ────────────────────────────────────────────────────────────────────

export const fetchNotes = async (partnerEntityId: number): Promise<Note[]> => {
  const { data } = await apiClient.get("/notes", { params: { partner_entity_id: partnerEntityId } });
  return normalizeList<Note>(data);
};

export const createNote = async (
  partnerEntityId: number,
  note: string,
  scheduleItemId?: number
): Promise<Note> => {
  const payload: Record<string, unknown> = {
    partner_entity_id: partnerEntityId,
    note,
  };
  if (scheduleItemId) payload.schedule_item_id = scheduleItemId;
  const { data } = await apiClient.post("/notes", { note: payload });
  return normalizeOne<Note>(data);
};

export const updateNote = async (id: number, note: string): Promise<Note> => {
  const { data } = await apiClient.patch(`/notes/${id}`, { note: { note } });
  return normalizeOne<Note>(data);
};

export const deleteNote = async (id: number): Promise<void> => {
  await apiClient.delete(`/notes/${id}`);
};

// ─── Availability Schedules ───────────────────────────────────────────────────

export const fetchAvailabilitySchedule = async (contactUserId: number): Promise<AvailabilitySchedule | null> => {
  const { data } = await apiClient.get("/availability_schedules", { params: { contact_user_id: contactUserId } });
  const list = normalizeList<AvailabilitySchedule>(data);
  return list.length > 0 ? list[0] : null;
};

// ─── Courier Availability Calendar ───────────────────────────────────────────

export interface CourierAvailabilityEntry {
  id:               number;
  name_1:           string;
  name_2:           string | null;
  availability:     import("types").AvailabilityMap;
  days_off:         Array<{ id: number; start_at: string; end_at: string; status: string }>;
  ad_hoc_schedules: Array<{ id: number; week_start_date: string; availability: import("types").AvailabilityMap }>;
}

export const fetchCourierAvailability = async (partnerId: number): Promise<CourierAvailabilityEntry[]> => {
  const { data } = await apiClient.get("/courier_availability", { params: { partner_id: partnerId } });
  return (data as { couriers: CourierAvailabilityEntry[] }).couriers;
};

export const createAvailabilitySchedule = async (params: {
  contact_user_id: number;
  availability: Record<string, Array<{ start: string; end: string }>>;
}): Promise<AvailabilitySchedule> => {
  const { data } = await apiClient.post("/availability_schedules", { availability_schedule: params });
  return normalizeOne<AvailabilitySchedule>(data);
};

export const updateAvailabilitySchedule = async (id: number, params: {
  availability: Record<string, Array<{ start: string; end: string }>>;
}): Promise<AvailabilitySchedule> => {
  const { data } = await apiClient.patch(`/availability_schedules/${id}`, { availability_schedule: params });
  return normalizeOne<AvailabilitySchedule>(data);
};

// ─── Ad-Hoc Availability Schedules ───────────────────────────────────────────

export const fetchAdHocAvailabilitySchedules = async (contactUserId: number): Promise<AdHocAvailabilitySchedule[]> => {
  const { data } = await apiClient.get("/ad_hoc_availability_schedules", { params: { contact_user_id: contactUserId } });
  return normalizeList<AdHocAvailabilitySchedule>(data);
};

export const createAdHocAvailabilitySchedule = async (params: {
  contact_user_id: number;
  week_start_date: string;
  availability:    Record<string, Array<{ start: string; end: string }>>;
}): Promise<AdHocAvailabilitySchedule> => {
  const { data } = await apiClient.post("/ad_hoc_availability_schedules", { ad_hoc_availability_schedule: params });
  return normalizeOne<AdHocAvailabilitySchedule>(data);
};

export const updateAdHocAvailabilitySchedule = async (id: number, params: {
  availability: Record<string, Array<{ start: string; end: string }>>;
}): Promise<AdHocAvailabilitySchedule> => {
  const { data } = await apiClient.patch(`/ad_hoc_availability_schedules/${id}`, { ad_hoc_availability_schedule: params });
  return normalizeOne<AdHocAvailabilitySchedule>(data);
};

export const deleteAdHocAvailabilitySchedule = async (id: number): Promise<void> => {
  await apiClient.delete(`/ad_hoc_availability_schedules/${id}`);
};

// ─── Days Off ─────────────────────────────────────────────────────────────────

export const fetchDaysOffs = async (contactUserId: number): Promise<DaysOff[]> => {
  const { data } = await apiClient.get("/days_offs", { params: { contact_user_id: contactUserId } });
  return normalizeList<DaysOff>(data);
};

export const createDaysOff = async (params: {
  contact_user_id: number;
  start_at:        string;
  end_at:          string;
}): Promise<DaysOff> => {
  const { data } = await apiClient.post("/days_offs", { days_off: params });
  return normalizeOne<DaysOff>(data);
};

export const updateDaysOff = async (
  id: number,
  params: { status?: DaysOffStatus; start_at?: string; end_at?: string },
): Promise<DaysOff> => {
  const { data } = await apiClient.patch(`/days_offs/${id}`, { days_off: params });
  return normalizeOne<DaysOff>(data);
};

export const deleteDaysOff = async (id: number): Promise<void> => {
  await apiClient.delete(`/days_offs/${id}`);
};

// ─── Blackout Days ────────────────────────────────────────────────────────────

export const fetchBlackoutDays = async (params: {
  partner_entity_id: number;
  future_only?: boolean;
}): Promise<BlackoutDay[]> => {
  const { data } = await apiClient.get("/blackout_days", { params });
  return normalizeList<BlackoutDay>(data);
};

export const createBlackoutDay = async (params: {
  partner_entity_id: number;
  date: string;
}): Promise<BlackoutDay> => {
  const { data } = await apiClient.post("/blackout_days", { blackout_day: params });
  return normalizeOne<BlackoutDay>(data);
};

export const deleteBlackoutDay = async (id: number): Promise<void> => {
  await apiClient.delete(`/blackout_days/${id}`);
};

// ─── Scheduler ────────────────────────────────────────────────────────────────

export const runScheduler = async (weekOffset: number = 0): Promise<{ created: number; skipped: number; errors: unknown[] }> => {
  const { data } = await apiClient.post("/scheduler/run", { week_offset: weekOffset });
  return data;
};

export const runRecipientScheduler = async (weekOffset: number = 0): Promise<{ created: number; skipped: number; errors: unknown[] }> => {
  const { data } = await apiClient.post("/scheduler/run_recipient", { week_offset: weekOffset });
  return data;
};

// ─── Admin Tools ─────────────────────────────────────────────────────────────

export const testEmailConnection = async (): Promise<{
  success: boolean;
  domain?: string;
  message: string;
}> => {
  const { data } = await apiClient.post("/tools/test_email_connection");
  return data;
};

export const sendTestEmail = async (to: string): Promise<{
  success: boolean;
  to?: string;
  message: string;
}> => {
  const { data } = await apiClient.post("/tools/send_test_email", { to });
  return data;
};

// ─── Partner Import ───────────────────────────────────────────────────────────

export interface ImportedPartner {
  id: number;
  name: string;
  type: string;
  active: boolean;
  created_at: string;
}

export interface ImportPartnersResult {
  summary: { processed: number; created: number; skipped: number; errored: number };
  partners: ImportedPartner[];
  errors: Array<{ row: number; error: string }>;
}

export const importPartners = async (file: File): Promise<ImportPartnersResult> => {
  const body = new FormData();
  body.append("file", file);
  const { data } = await apiClient.post("/tools/import_partners", body);
  return data;
};

// ─── Dev/Stage Tools ─────────────────────────────────────────────────────────

export const purgeScheduleItems = async (params: {
  start_date: string;
  end_date:   string;
}): Promise<{ deleted: number }> => {
  const { data } = await apiClient.delete("/tools/purge_schedule_items", { data: params });
  return data;
};

// ─── Mailer Tools (admin only) ───────────────────────────────────────────────

export const fetchCronJobs = async (): Promise<CronJobStatus[]> => {
  const { data } = await apiClient.get("/mailer_tools/cron_jobs");
  return data.cron_jobs;
};

export const triggerCronJob = async (key: string): Promise<{ queued: boolean; key: string }> => {
  const { data } = await apiClient.post(`/mailer_tools/cron_jobs/${key}`);
  return data;
};

export const fetchEmailLogs = async (params?: {
  page?:       number;
  per_page?:   number;
  status?:     string;
  mailer_key?: string;
}): Promise<EmailLogsResponse> => {
  const { data } = await apiClient.get("/mailer_tools/email_logs", { params });
  return data;
};

// Builds a URL to the JWT-gated GoodJob dashboard, mounted outside /api/v1
// (see config/routes.rb + app/constraints/good_job_dashboard_constraint.rb).
// The token only needs to ride along on this first request — the
// dashboard's own session cookie carries auth for every click after that.
export const goodJobDashboardUrl = (): string => {
  const apiBase = process.env.REACT_APP_API_URL ?? "http://localhost:3001/api/v1";
  const origin  = apiBase.replace(/\/api\/v1\/?$/, "");
  const token   = store.getState().auth.token ?? "";
  return `${origin}/good_job?token=${encodeURIComponent(token)}`;
};

// ─── JSONAPI normalizers ──────────────────────────────────────────────────────

function normalizeOne<T>(response: { data: { attributes: T; id: string } }): T & { id: number } {
  return { ...response.data.attributes, id: Number(response.data.id) };
}

function normalizeList<T>(response: { data: Array<{ attributes: T; id: string }> }): Array<T & { id: number }> {
  return response.data.map((item) => ({ ...item.attributes, id: Number(item.id) }));
}